/**
 * Image URL utility with Cloudflare Image Transform support.
 *
 * Handles both full URLs (legacy) and bare R2 keys, and appends
 * /cdn-cgi/image/ transform parameters for deployed environments.
 */

import { ENV } from '@/config/env';

type ImageSize = 'thumbnail' | 'medium' | 'full' | 'original';

const TRANSFORM_PRESETS: Record<Exclude<ImageSize, 'original'>, string> = {
  thumbnail: 'w=150,h=150,fit=cover,format=auto,quality=75',
  medium: 'w=600,h=600,fit=scale-down,format=auto,quality=80',
  full: 'w=1200,fit=scale-down,format=auto,quality=85',
};

/**
 * Build an image URL with optional Cloudflare Image Transforms.
 *
 * @param r2KeyOrUrl - An R2 object key ("profile-photos/abc.webp") or a full URL.
 * @param size       - Preset size. Defaults to 'original' (no transforms).
 * @returns The resolved URL string, or empty string for null/undefined input.
 */
export function imageUrl(
  r2KeyOrUrl: string | null | undefined,
  size: ImageSize = 'original',
): string {
  if (!r2KeyOrUrl) return '';

  // Determine the base origin and path
  let origin: string;
  let path: string;

  if (r2KeyOrUrl.startsWith('http://') || r2KeyOrUrl.startsWith('https://')) {
    // Already a full URL -- extract origin and path
    try {
      const parsed = new URL(r2KeyOrUrl);
      origin = parsed.origin;
      path = parsed.pathname.replace(/^\//, ''); // strip leading slash
    } catch {
      // Malformed URL -- return as-is
      return r2KeyOrUrl;
    }
  } else {
    // Bare R2 key
    if (!ENV.UPLOAD_URL) {
      // Local development -- serve through the gateway at /uploads/{key}
      return `${ENV.API_URL}/uploads/${r2KeyOrUrl}`;
    }
    origin = ENV.UPLOAD_URL;
    path = r2KeyOrUrl;
  }

  // Skip transforms in development or when 'original' is requested
  if (size === 'original' || ENV.APP_ENV === 'development') {
    return `${origin}/${path}`;
  }

  const params = TRANSFORM_PRESETS[size];
  return `${origin}/cdn-cgi/image/${params}/${path}`;
}
